/* @flow */
import type {
  ReduxState,
  Action,
  Fetchable,
  Featured,
  MediaSearchParams,
  MediaSearchFullParams,
  MediaSearchResults,
  Aggregations,
  MediaAggregationKey,
} from '../../types';
import { mediaSearchParamsProps } from '../../types/MediaSearchParams';
import _ from 'lodash';
// ===========================
//   STATE TYPE
// ===========================
export type MediaSearchState = {
  featured: Fetchable<Featured>,
  searchParams: MediaSearchParams,
  submittedSearchParams: MediaSearchFullParams,
  sections: Fetchable<MediaSearchResults>,
  pageScroll: number,
  expandedCategories: {
    [categoryId: number]: boolean,
  },
  slideIndexByCategory: {
    [categoryId: number]: number,
  },
};

const initialPageScroll = 0;
const initialExpandedCategories = {};
const initialSlideIndexByCategory = {};

// ===========================
//   INITIAL STATE
// ===========================
export const initialState: MediaSearchState = {
  featured: { fetching: false },
  searchParams: { categoryIds: [], news: false },
  submittedSearchParams: { categoryIds: [], news: false, text: '' },
  categories: { fetching: false },
  sections: { fetching: false },
  pageScroll: initialPageScroll,
  expandedCategories: initialExpandedCategories,
  slideIndexByCategory: initialSlideIndexByCategory,
};

const isStaleRequest = (params?: MediaSearchParams, requestParams: MediaSearchParams) => {
  return !_.isEqual(params, requestParams);
  // return !(
  //   params &&
  //   isEqual({ ...params, pagination: undefined }, { ...requestParams, pagination: undefined })
  // );
};

// ===========================
//   REDUCER
// ===========================
export default (state: MediaSearchState = initialState, action: Action): MediaSearchState => {
  switch (action.type) {
    case 'FETCH_FEATURED_REQUEST': {
      return {
        ...state,
        featured: { data: state.featured.data, fetching: true },
      };
    }
    case 'FETCH_FEATURED_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        featured: { data, fetching: false },
        pageScroll: initialPageScroll,
        expandedCategories: initialExpandedCategories,
        slideIndexByCategory: initialSlideIndexByCategory,
      };
    }
    case 'FETCH_FEATURED_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        featured: { error, fetching: false },
        pageScroll: initialPageScroll,
        expandedCategories: initialExpandedCategories,
        slideIndexByCategory: initialSlideIndexByCategory,
      };
    }
    // NEWS
    case 'SET_FEATURED_MEDIA_SEARCH': {
      const fullSearchParams: MediaSearchFullParams = action.payload;
      const searchParams: MediaSearchParams = _.pick(fullSearchParams, mediaSearchParamsProps);
      return { ...state, searchParams };
    }
    case 'FETCH_CATEGORIES_SUCCESS': {
      // const data = action.payload;
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          // categoryIds: data.map(_ => _.id),
          categoryIds: [],
          pagination: {
            start: 0,
            end: 1000,
          },
          pageScroll: initialPageScroll,
          expandedCategories: initialExpandedCategories,
          slideIndexByCategory: initialSlideIndexByCategory,
        },
      };
    }
    case 'PERFORM_FEATURED_MEDIA_SEARCH_REQUEST': {
      const fullSearchParams: MediaSearchFullParams = action.payload;
      const searchParams: MediaSearchParams = _.pick(fullSearchParams, mediaSearchParamsProps);
      return {
        ...state,
        searchParams,
        submittedSearchParams: fullSearchParams,
        sections: { data: state.sections.data, fetching: true },
      };
    }
    case 'PERFORM_FEATURED_MEDIA_SEARCH_SUCCESS': {
      if (!isStaleRequest(state.submittedSearchParams, action.meta.initialPayload)) {
        // const categoryIds = keys(action.payload).map(parseInt);
        const data = action.payload;
        return {
          ...state,
          sections: { data, fetching: false },
          pageScroll: initialPageScroll,
          expandedCategories: initialExpandedCategories,
          slideIndexByCategory: initialSlideIndexByCategory,
        };
      } else {
        // ignore results
        return state;
      }
    }
    case 'PERFORM_FEATURED_MEDIA_SEARCH_FAILURE': {
      if (!isStaleRequest(state.submittedSearchParams, action.meta.initialPayload)) {
        const error = action.payload;
        return {
          ...state,
          sections: { error, fetching: false },
          pageScroll: initialPageScroll,
          expandedCategories: initialExpandedCategories,
          slideIndexByCategory: initialSlideIndexByCategory,
        };
      } else {
        // ignore results
        return state;
      }
    }
    case 'FEATURED_MEDIA_SCROLL_PAGE': {
      const pageScroll = action.payload || 0;
      return {
        ...state,
        pageScroll,
      };
    }
    case 'FEATURED_MEDIA_TOGGLE_EXPAND_CATEGORY': {
      const categoryId = action.payload;
      return {
        ...state,
        expandedCategories: {
          ...state.expandedCategories,
          [categoryId]: !state.expandedCategories[categoryId],
        },
      };
    }
    case 'FEATURED_MEDIA_SET_CATEGORY_SLIDE': {
      const { categoryId, slideIndex } = action.payload;
      return {
        ...state,
        slideIndexByCategory: {
          ...state.slideIndexByCategory,
          [categoryId]: slideIndex || 0,
        },
      };
    }
    case 'LOGOUT_SUCCESS':
      return initialState;

    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getFeatured = (state: ReduxState): Fetchable<Featured> => {
  return state.featuredNews.featured;
};

export const getSelectedCategoryIds = (state: ReduxState): number[] => {
  return state.featuredNews.searchParams.categoryIds;
};

export const getSections = (state: ReduxState): Fetchable<MediaSearchResults> => {
  return state.featuredNews.sections;
};

export const getAggregations = (
  state: ReduxState
): Fetchable<Aggregations<MediaAggregationKey>> => {
  const { fetching, error, data } = state.featuredNews.sections;
  return {
    fetching,
    error,
    data: data && data.aggregations,
  };
};

export const getFeaturedNewsSearchParams = (state: ReduxState): MediaSearchFullParams => {
  // $FlowFixMe
  return _.merge({}, { flags: {} }, state.featuredNews.searchParams);
};

export const getSubmittedFeaturedNewsSearchParams = (state: ReduxState): MediaSearchFullParams => {
  // $FlowFixMe
  return _.merge({}, { flags: {} }, state.featuredNews.submittedSearchParams);
};
